import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import ImageRoundedIcon from '@mui/icons-material/ImageRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import {
  loadQuickActionCapabilities,
  sendQuickActionAttachment,
} from './patientQuickActionsService'

const ACCEPTED_FILE_TYPES = 'image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt'
const MAX_TOTAL_BYTES = 16 * 1024 * 1024 // WhatsApp media ceiling (~16MB for documents)

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function isImageFile(file) {
  return `${file?.type || ''}`.startsWith('image/')
}

export default function SendAttachmentDialog({ open, onClose, contactContext, patientName }) {
  const [files, setFiles] = useState([])
  const [caption, setCaption] = useState('')
  const [sending, setSending] = useState(false)
  const [capabilities, setCapabilities] = useState(null)
  const [loadingCapabilities, setLoadingCapabilities] = useState(false)
  const fileInputRef = useRef(null)

  const resolvedName = patientName || capabilities?.targetDisplayName || 'the patient'

  const loadCapabilities = useCallback(async () => {
    if (!contactContext) return
    setLoadingCapabilities(true)
    const result = await loadQuickActionCapabilities(contactContext)
    setCapabilities(result)
    setLoadingCapabilities(false)
  }, [contactContext])

  useEffect(() => {
    if (open) {
      setFiles([])
      setCaption('')
      setCapabilities(null)
      loadCapabilities()
    }
  }, [open, loadCapabilities])

  const handlePickFiles = useCallback((event) => {
    const picked = Array.from(event?.target?.files || [])
    if (picked.length > 0) {
      setFiles((current) => [...current, ...picked])
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const removeFile = useCallback((index) => {
    setFiles((current) => current.filter((_, i) => i !== index))
  }, [])

  const totalBytes = files.reduce((sum, file) => sum + (file?.size || 0), 0)
  const overSizeLimit = totalBytes > MAX_TOTAL_BYTES

  const whatsappEnabled = capabilities?.ok && capabilities?.whatsappEnabled
  const warmWindowOpen = !!capabilities?.warmWindowOpen
  const canSend =
    whatsappEnabled &&
    !overSizeLimit &&
    !sending &&
    (files.length > 0 || caption.trim().length > 0)

  const handleSend = useCallback(async () => {
    setSending(true)
    const result = await sendQuickActionAttachment(contactContext, caption.trim(), files)
    setSending(false)
    if (result?.ok) {
      toast.success(`Attachment sent to ${resolvedName} on WhatsApp.`)
      onClose?.()
    } else {
      toast.error(result?.message || 'Unable to send the attachment.')
    }
  }, [contactContext, caption, files, resolvedName, onClose])

  return (
    <Dialog open={!!open} onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <AttachFileRoundedIcon sx={{ color: '#4338ca' }} />
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>
            Send attachment
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            to {resolvedName}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={sending}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label="Close"
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {/* Channel row — WhatsApp only in this phase */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Channel:
            </Typography>
            <Chip
              icon={<WhatsAppIcon />}
              label="WhatsApp"
              size="small"
              sx={{ bgcolor: 'rgba(0,168,132,0.12)', color: '#0f766e', fontWeight: 600 }}
            />
            <Tooltip title="Email delivery is coming in a later phase">
              <span>
                <Chip label="Email — soon" size="small" variant="outlined" disabled />
              </span>
            </Tooltip>
          </Box>

          {loadingCapabilities && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Checking WhatsApp availability…
              </Typography>
            </Box>
          )}

          {capabilities && !capabilities.ok && (
            <Alert severity="error">{capabilities.message}</Alert>
          )}

          {capabilities?.ok && !whatsappEnabled && (
            <Alert severity="warning">
              {capabilities.whatsappReason || 'WhatsApp is not available for this patient.'}
            </Alert>
          )}

          {capabilities?.ok && whatsappEnabled && !warmWindowOpen && (
            <Alert severity="info">
              The 24-hour chat window is closed, so files will be sent to the patient as secure document
              links.
            </Alert>
          )}

          {/* File picker */}
          <Box>
            <input
              ref={fileInputRef}
              type="file"
              hidden
              multiple
              accept={ACCEPTED_FILE_TYPES}
              onChange={handlePickFiles}
            />
            <Button
              variant="outlined"
              startIcon={<AttachFileRoundedIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={{ textTransform: 'none' }}
            >
              Add file (PDF / image)
            </Button>
          </Box>

          {files.length > 0 && (
            <Stack spacing={1}>
              {files.map((file, index) => (
                <Paper
                  key={`${file.name}-${index}`}
                  variant="outlined"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 2 }}
                >
                  {isImageFile(file) ? (
                    <ImageRoundedIcon sx={{ color: '#4338ca' }} />
                  ) : (
                    <DescriptionRoundedIcon sx={{ color: '#4338ca' }} />
                  )}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" noWrap title={file.name}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {formatBytes(file.size)}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => removeFile(index)} disabled={sending}>
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </Paper>
              ))}
            </Stack>
          )}

          {overSizeLimit && (
            <Alert severity="warning">
              Total size {formatBytes(totalBytes)} exceeds the {formatBytes(MAX_TOTAL_BYTES)} WhatsApp
              limit. Remove or compress a file.
            </Alert>
          )}

          <TextField
            label="Message (optional)"
            placeholder="Add a caption to send with the attachment"
            value={caption}
            onChange={(event) => setCaption(event.target.value)}
            multiline
            minRows={2}
            maxRows={5}
            fullWidth
            disabled={sending}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={sending} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={!canSend}
          startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
          sx={{ textTransform: 'none', bgcolor: '#00a884', '&:hover': { bgcolor: '#16a34a' } }}
        >
          {sending ? 'Sending…' : 'Send on WhatsApp'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
